import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Stack, Grid, Button, TextField, MenuItem, IconButton, Chip,
  Tooltip, Alert, Autocomplete, Checkbox, ListItemText, FormControl,
  InputLabel, Select, OutlinedInput, Divider, Typography,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon,
  PersonSearch as TeacherIcon, ClassOutlined as ClassIcon,
  MenuBook as SubjectIcon, CalendarMonth as YearIcon,
  Lock as LockIcon, AdminPanelSettings as RolesIcon,
  Group as UsersIcon, OpenInNew as OpenIcon,
  SupervisorAccount as ClassTeacherIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import { useAuth } from '../../context/AuthContext';

// Teacher Assignments panel — Examination Settings sub-section.
//
// Design rules (driven by the user's feedback):
//   • Academic year is FIXED to the exam module's active period — it's
//     pulled from the settings the admin already chose and displayed
//     read-only here. No accidental cross-year edits.
//   • Teacher dropdown only lists users who already carry the
//     `exam_home` page in their permissions — i.e. accounts that are
//     actually set up to enter scores. Backend enforces this filter
//     so the picker can never include a finance-only user.
//   • Class dropdown only lists classes that actually have one or
//     more subjects registered for the active year (no dead-end
//     selections).
//   • Subject picker is a multi-select with checkboxes and a
//     "Select all" toggle so a teacher who owns every subject in a
//     class can be set up in one click instead of N.
// The exam types an assignment can be pinned to. Blank — every type —
// is handled separately because it is the default, not one of these.
const EXAM_STATES = ['Term Exam', 'Mock', 'Mid-Term Test'];

// How an assignment's coverage reads in the table.
const stateLabel = (s) => (s ? `${s} only` : 'Every exam');

export default function TeacherAssignmentsPanel({ scopeYear, scopeTerm } = {}) {
  const { user } = useAuth();
  // Only super_admin can reach the Roles and Users screens (RoleGuard
  // in App.js). For everyone else we hide the deep-link buttons in
  // the empty-state prompts and tell them to escalate to an admin.
  const isAdmin = user?.role === 'super_admin';

  const [teachers, setTeachers]       = useState([]);
  const [teacherRoles, setTeacherRoles] = useState([]);
  const [requiresRole, setRequiresRole]   = useState(false);
  const [requiresUsers, setRequiresUsers] = useState(false);
  const [classOptions, setClassOptions] = useState([]);
  const [teacher,  setTeacher]        = useState(null);
  // Scope — driven by the Examination Settings scope banner when
  // rendered inside that page. Falls back to the active exam period
  // pulled from the backend on first mount so this panel still works
  // standalone (e.g. if reused elsewhere).
  const [accYer,   setAccYer]         = useState(scopeYear || '');
  const [term,     setTerm]           = useState(scopeTerm || '');
  const [yearLoading, setYearLoading] = useState(!scopeYear);

  const [rows, setRows]               = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);

  // "Add" form state
  const [addCla, setAddCla]               = useState('');
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [pickedSubs, setPickedSubs]       = useState([]);
  const [adding, setAdding]               = useState(false);
  // Which exam this assignment is for. '' — the default — means every
  // exam type, which is how assignments have always worked and what
  // end-of-term marking relies on. Naming one narrows the teacher to it.
  const [addState, setAddState]           = useState('');

  // ── Class teachers (homeroom / form-master) ──────────────────
  // One teacher per class per year — they'll own the remarks sheet
  // on the teacher portal. Keyed by class name for O(1) lookup when
  // rendering the picker row for each class in scope.
  const [classTeachers, setClassTeachers] = useState({});   // cla -> {user_id, teacher_name, teacher_email}
  const [ctSavingCla, setCtSavingCla]     = useState('');

  // ── initial loads ────────────────────────────────────────────
  const loadTeachers = useCallback(async () => {
    try {
      const { data } = await API.get('/teacher-assignments/teachers');
      setTeachers(data?.teachers || []);
      setTeacherRoles(data?.teacher_roles || []);
      setRequiresRole(!!data?.requires_role);
      setRequiresUsers(!!data?.requires_users);
    } catch (e) { /* surfaced via empty list */ }
  }, []);
  useEffect(() => { loadTeachers(); }, [loadTeachers]);
  // Load the active period on mount ONLY when no scope was passed in.
  // If scope props are present, they drive the panel entirely.
  useEffect(() => {
    if (scopeYear) return;
    setYearLoading(true);
    API.get('/teacher-assignments/active-year')
      .then(r => {
        setAccYer(r.data?.acc_yer || '');
        setTerm(r.data?.term || '');
      })
      .catch(() => {})
      .finally(() => setYearLoading(false));
  }, [scopeYear]);
  // Track scope prop changes from the parent scope banner.
  useEffect(() => { if (scopeYear !== undefined) setAccYer(scopeYear || ''); }, [scopeYear]);
  useEffect(() => { if (scopeTerm !== undefined) setTerm(scopeTerm || ''); }, [scopeTerm]);

  // ── classes (only those with registered subjects in this exact
  // term + year — stale registrations from other terms are excluded). ──
  useEffect(() => {
    if (!accYer || !term) { setClassOptions([]); return; }
    API.get('/teacher-assignments/classes-with-subjects',
      // Narrowed to the chosen exam type, so picking "Mock" lists only
      // the classes that actually have a mock registered — rather than
      // offering a class whose mock does not exist yet.
      { params: { acc_yer: accYer, term, state: addState || undefined } })
      .then(r => setClassOptions(r.data?.classes || []))
      .catch(() => setClassOptions([]));
  }, [accYer, term, addState]);

  // ── class teachers for the active year ───────────────────────
  const loadClassTeachers = useCallback(async () => {
    if (!accYer) { setClassTeachers({}); return; }
    try {
      const { data } = await API.get('/teacher-assignments/class-teachers',
        { params: { acc_yer: accYer } });
      const map = {};
      for (const r of (data?.class_teachers || [])) map[r.cla] = r;
      setClassTeachers(map);
    } catch { setClassTeachers({}); }
  }, [accYer]);
  useEffect(() => { loadClassTeachers(); }, [loadClassTeachers]);

  // Assign or replace a class teacher (both go through the same PUT).
  const setClassTeacher = async (cla, teacherOrNull) => {
    if (!accYer || !cla) return;
    setCtSavingCla(cla);
    try {
      if (teacherOrNull) {
        await API.put('/teacher-assignments/class-teachers',
          { user_id: teacherOrNull.id, acc_yer: accYer, cla });
      } else {
        await API.delete('/teacher-assignments/class-teachers',
          { params: { acc_yer: accYer, cla } });
      }
      await loadClassTeachers();
    } catch (e) {
      swal.error(swal.pickError(e, 'Save failed'));
    } finally { setCtSavingCla(''); }
  };

  // ── (re)load assignment rows when teacher or year changes ───
  const reloadRows = useCallback(async () => {
    if (!teacher || !accYer) { setRows([]); return; }
    setLoadingRows(true);
    try {
      const { data } = await API.get('/teacher-assignments', {
        params: { user_id: teacher.id, acc_yer: accYer },
      });
      setRows(data?.assignments || []);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load assignments'));
    } finally {
      setLoadingRows(false);
    }
  }, [teacher, accYer]);

  useEffect(() => { reloadRows(); }, [reloadRows]);

  // ── subject options for the chosen class — scoped to the active
  // (acc_yer, term) so other-term registrations don't appear. ──
  useEffect(() => {
    setPickedSubs([]);
    setSubjectOptions([]);
    if (!addCla || !accYer || !term) return;
    API.get('/teacher-assignments/subjects-for-class', {
      params: { cla: addCla, acc_yer: accYer, term, state: addState || undefined },
    })
      .then(r => setSubjectOptions(r.data?.subjects || []))
      .catch(() => setSubjectOptions([]));
  }, [addCla, accYer, term, addState]);

  // Subjects this teacher is ALREADY assigned to in the chosen class —
  // we use this to grey-out / pre-check them so the admin can see at
  // a glance what's already done.
  //
  // Matched per exam type: a teacher already covering a subject for
  // "every exam" has nothing to add for the mock, but one assigned only
  // to the Term Exam still needs a separate mock row — so an existing
  // all-types row blocks, and a differently-typed one does not.
  const existingForClass = useMemo(() => {
    if (!addCla) return new Set();
    return new Set(
      rows
        .filter(r => r.cla === addCla)
        .filter(r => {
          const st = r.exam_state || '';
          return st === '' || st === (addState || '');
        })
        .map(r => r.sub));
  }, [rows, addCla, addState]);

  // Subjects available to actually add = options minus already-owned.
  const availableSubs = useMemo(
    () => subjectOptions.filter(s => !existingForClass.has(s)),
    [subjectOptions, existingForClass]
  );

  const allAvailableSelected = availableSubs.length > 0
    && availableSubs.every(s => pickedSubs.includes(s));

  // Sentinel value used by the "Select all" row inside the Select.
  // We never store it in state — onChange intercepts it and toggles
  // the real subject array instead.
  const SELECT_ALL = '__select_all__';

  // ── add many assignment rows at once ─────────────────────────
  const handleAdd = async () => {
    if (!teacher)              return swal.warning('Pick a teacher first');
    if (!addCla)               return swal.warning('Pick a class');
    if (pickedSubs.length === 0) return swal.warning('Tick at least one subject');
    setAdding(true);
    try {
      const items = pickedSubs.map(sub => ({ cla: addCla, sub }));
      const { data } = await API.post('/teacher-assignments', {
        user_id: teacher.id, acc_yer: accYer, items,
        exam_state: addState || '',
      });
      swal.success(data?.message || `Added ${items.length} subject(s)`);
      setPickedSubs([]);
      reloadRows();
    } catch (e) {
      swal.error(swal.pickError(e, 'Add failed'));
    } finally {
      setAdding(false);
    }
  };

  // ── remove a single assignment row ───────────────────────────
  const handleRemove = async (row) => {
    const ok = await swal.confirmDelete(
      `${row.sub} (${row.cla}) from ${row.teacher_name || 'this teacher'}`
    );
    if (!ok) return;
    try {
      await API.delete(`/teacher-assignments/${row.id}`);
      reloadRows();
    } catch (e) {
      swal.error(swal.pickError(e, 'Remove failed'));
    }
  };

  const classChip = useMemo(() => {
    const set = new Set(rows.map(r => r.cla));
    return [...set].sort();
  }, [rows]);

  // ── render ───────────────────────────────────────────────────

  // Step 1 prompt — no role has been flagged as a teacher role yet.
  // Block the whole panel; only super-admins get the deep-link
  // buttons since the Roles + Users screens are super-admin only.
  if (requiresRole) {
    return (
      <Box>
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}
          icon={<RolesIcon />}>
          <Typography fontWeight={800} sx={{ mb: 0.5 }}>
            No teacher role exists yet
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Before you can assign subjects, an administrator must create
            (or pick) a role that represents teachers. {isAdmin
              ? <>Go to the <b>Roles</b> screen, open the role you want to use for
                  teachers, and tick <b>"This role represents teachers"</b>. Then
                  assign that role to each teacher in the <b>Users</b> screen.
                  They'll appear here automatically.</>
              : <>Please contact your system administrator to flag a role as the
                  teacher role and assign teacher accounts to it. Once that's
                  done you'll be able to assign subjects here.</>}
          </Typography>
          {isAdmin && (
            <Stack direction="row" spacing={1}>
              <Button component={RouterLink} to="/roles" variant="contained"
                size="small" startIcon={<RolesIcon />} endIcon={<OpenIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: 'none', fontWeight: 800 }}>
                Open Roles screen
              </Button>
              <Button component={RouterLink} to="/users" variant="outlined"
                size="small" startIcon={<UsersIcon />} endIcon={<OpenIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                Open Users screen
              </Button>
            </Stack>
          )}
        </Alert>
      </Box>
    );
  }

  // Step 2 prompt — a teacher role exists but no user carries it.
  if (requiresUsers) {
    return (
      <Box>
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}
          icon={<UsersIcon />}>
          <Typography fontWeight={800} sx={{ mb: 0.5 }}>
            No users are on a teacher role yet
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            The role
            {teacherRoles.length === 1
              ? <> <b>{teacherRoles[0].label}</b> is</>
              : <>s {teacherRoles.map((r, i) => <span key={r.id}><b>{r.label}</b>{i < teacherRoles.length - 1 ? ', ' : ''}</span>)} are</>}
            {' '}flagged as a teacher role, but nobody is assigned to
            {teacherRoles.length === 1 ? ' it' : ' any of them'} yet.
            {isAdmin
              ? <> Open the <b>Users</b> screen, edit each teacher's account, and
                  give them the teacher role.</>
              : <> Please contact your system administrator to assign the
                  teacher role to the relevant accounts.</>}
          </Typography>
          <Stack direction="row" spacing={1}>
            {isAdmin && (
              <Button component={RouterLink} to="/users" variant="contained"
                size="small" startIcon={<UsersIcon />} endIcon={<OpenIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: 'none', fontWeight: 800 }}>
                Open Users screen
              </Button>
            )}
            <Button onClick={loadTeachers}
              variant={isAdmin ? 'outlined' : 'contained'} size="small"
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              {isAdmin ? `I've assigned — reload` : 'Reload'}
            </Button>
          </Stack>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* ─────── Class Teachers (homeroom / form-master) ─────── */}
      <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <ClassTeacherIcon color="primary" />
          <Typography sx={{ fontWeight: 800, fontSize: 15 }}>Class Teachers</Typography>
          <Chip size="small" variant="outlined"
            label={`${Object.keys(classTeachers).length} of ${classOptions.length} set`}
            sx={{ height: 20, fontWeight: 700, fontSize: 10 }} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          One class teacher per class — they'll own the report-card remarks sheet on the teacher portal.
          Classes that already have subject teachers assigned appear below.
        </Typography>

        {classOptions.length === 0 ? (
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            {!accYer
              ? 'No active academic period yet.'
              : 'No classes have subjects registered for this year+term. Register subjects first.'}
          </Alert>
        ) : (
          <Grid container spacing={1.2}>
            {classOptions.map(({ cla }) => {
              const current = classTeachers[cla];
              const currentTeacher = current
                ? teachers.find(t => t.id === current.user_id) || {
                    id: current.user_id,
                    full_name: current.teacher_name,
                    email: current.teacher_email,
                  }
                : null;
              const saving = ctSavingCla === cla;
              return (
                <Grid item xs={12} sm={6} md={4} key={cla}>
                  <Box sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                    p: 1, display: 'flex', alignItems: 'center', gap: 0.8,
                    bgcolor: current ? 'primary.50' : 'transparent',
                  }}>
                    <Chip label={cla} size="small" color="primary"
                      sx={{ fontWeight: 800, minWidth: 62, flexShrink: 0 }} />
                    <Autocomplete
                      size="small"
                      fullWidth
                      disabled={saving}
                      options={teachers}
                      value={currentTeacher}
                      onChange={(_, v) => setClassTeacher(cla, v)}
                      getOptionLabel={(o) => o ? (o.full_name || o.email) : ''}
                      isOptionEqualToValue={(a, b) => a?.id === b?.id}
                      noOptionsText="No teacher accounts"
                      renderInput={(params) => (
                        <TextField {...params} size="small" variant="standard"
                          placeholder={saving ? 'Saving…' : 'Pick teacher…'}
                          InputProps={{ ...params.InputProps, disableUnderline: true }} />
                      )} />
                    {current && (
                      <Tooltip title="Clear class teacher">
                        <IconButton size="small" color="error"
                          disabled={saving}
                          onClick={() => setClassTeacher(cla, null)}
                          sx={{ p: 0.4, flexShrink: 0 }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        Assign teachers to the specific subjects they own. Once assigned,
        non-admin teachers only see those classes/subjects when they open
        the Score Sheet or the Subject Registration page — admins still
        see everything.
      </Alert>

      {/* Year / teacher row */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={5}>
          <Autocomplete
            size="small"
            options={teachers}
            value={teacher}
            onChange={(_, v) => setTeacher(v)}
            getOptionLabel={(o) => o ? `${o.full_name || o.email} (${o.role_label || 'no role'})` : ''}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            noOptionsText="No teachers found — assign the teacher role to users in the Users screen first."
            renderInput={(params) => (
              <TextField {...params} label="Teacher account" size="small"
                placeholder="Type to search assignable accounts" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField fullWidth size="small" label="Active period (locked)"
            value={yearLoading
              ? 'Loading…'
              : (accYer || term
                  ? `${accYer || '—'} · ${(term || '').replace(/_/g, ' ') || '—'}`
                  : '— no active period —')}
            InputProps={{
              readOnly: true,
              startAdornment: <YearIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />,
              endAdornment: <Tooltip title="Locked to the active examination period (year + term). Change it in the Academic Period section above."><LockIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></Tooltip>,
            }}
            helperText="Only subjects registered for this year + term appear below." />
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {teacher && (
            <>
              <Chip size="small" color="primary"
                icon={<TeacherIcon sx={{ fontSize: 14 }} />}
                label={teacher.full_name || teacher.email} />
              <Chip size="small" variant="outlined"
                label={`${rows.length} assignment(s)`} />
              {classChip.length > 0 && (
                <Chip size="small" variant="outlined"
                  label={`${classChip.length} class(es)`} />
              )}
            </>
          )}
        </Grid>
      </Grid>

      {/* Add form — class + multi-select subjects */}
      <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5,
        border: '1px dashed', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Grid container spacing={1.2} alignItems="center">
          {/* Which exam this assignment is for.
              "Every exam" is the default and is how assignments have
              always worked — the same teacher marks the mock and the
              terminal. Naming one exam is for the case where they
              differ: a mock marked by somebody else. */}
          <Grid item xs={12} sm={3}>
            <TextField select fullWidth size="small" label="For which exam"
              value={addState}
              onChange={e => { setAddState(e.target.value); setAddCla(''); setPickedSubs([]); }}
              disabled={!teacher || !accYer}
              helperText={addState
                ? `Only ${addState} — this teacher gets no other exam for these subjects.`
                : 'Every exam type (Term Exam, Mock, Mid-Term)'}>
              <MenuItem value="">Every exam type</MenuItem>
              {EXAM_STATES.map(s => <MenuItem key={s} value={s}>{s} only</MenuItem>)}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField select fullWidth size="small" label="Class"
              value={addCla} onChange={e => setAddCla(e.target.value)}
              disabled={!teacher || !accYer || classOptions.length === 0}
              helperText={classOptions.length === 0 && accYer
                ? (addState
                    ? `No class has a ${addState} registered for this term yet.`
                    : 'No classes have subjects registered for this year yet.')
                : 'Only classes with registered subjects appear here.'}
              InputProps={{
                startAdornment: <ClassIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />,
              }}>
              <MenuItem value=""><i>Select class…</i></MenuItem>
              {classOptions.map(c => (
                <MenuItem key={c.cla} value={c.cla}>
                  {c.cla} <Box component="span" sx={{ ml: 1, color: 'text.disabled', fontSize: 11 }}>· {c.n_subjects} subject(s)</Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small"
              disabled={!addCla || subjectOptions.length === 0}>
              <InputLabel id="ta-subs-label">Subjects (tick to assign)</InputLabel>
              <Select
                labelId="ta-subs-label"
                multiple
                value={pickedSubs}
                onChange={(e) => {
                  const v = e.target.value;
                  const arr = typeof v === 'string' ? v.split(',') : v;
                  // Intercept the "Select all" sentinel — toggle the
                  // real picks and never let the sentinel itself land
                  // in state.
                  if (arr.includes(SELECT_ALL)) {
                    setPickedSubs(allAvailableSelected ? [] : availableSubs);
                    return;
                  }
                  setPickedSubs(arr);
                }}
                input={<OutlinedInput label="Subjects (tick to assign)" />}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                    {selected.length === 0
                      ? <span style={{ color: '#999' }}>Tick subjects…</span>
                      : selected.map(v => (
                        <Chip key={v} label={v} size="small"
                          sx={{ height: 20, fontWeight: 600 }} />
                      ))}
                  </Stack>
                )}
                MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                startAdornment={<SubjectIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />}
              >
                {/* Select-all row — uses a sentinel value so MUI's
                    own multi-select machinery routes the click through
                    onChange (where we intercept it). */}
                <MenuItem value={SELECT_ALL} dense
                  sx={{ bgcolor: 'grey.100', fontWeight: 700 }}>
                  <Checkbox checked={allAvailableSelected}
                    indeterminate={!allAvailableSelected && pickedSubs.length > 0}
                    size="small" />
                  <ListItemText primary={`Select all (${availableSubs.length})`} />
                </MenuItem>
                <Divider />
                {subjectOptions.map(s => {
                  const already = existingForClass.has(s);
                  return (
                    <MenuItem key={s} value={s} disabled={already} dense>
                      <Checkbox checked={pickedSubs.indexOf(s) > -1} size="small" />
                      <ListItemText
                        primary={s}
                        secondary={already ? 'already assigned' : null}
                        primaryTypographyProps={{ sx: { fontWeight: already ? 400 : 600 } }} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={<AddIcon />}
              disabled={!teacher || !accYer || !addCla || pickedSubs.length === 0 || adding}
              onClick={handleAdd}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              {adding ? 'Adding…'
                : pickedSubs.length > 1 ? `Add ${pickedSubs.length} subjects` : 'Add subject'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Current assignments table */}
      <Box sx={{ minHeight: 80 }}>
        {!teacher && (
          <Alert severity="info">Pick a teacher above to see and edit their assignments.</Alert>
        )}
        {teacher && loadingRows && (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={22} /></Box>
        )}
        {teacher && !loadingRows && rows.length === 0 && (
          <Alert severity="warning" variant="outlined">
            No assignments yet for {teacher.full_name || teacher.email} in {accYer || 'this year'}.
            Use the form above to add the first one.
          </Alert>
        )}
        {teacher && !loadingRows && rows.length > 0 && (
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 800 }}>Class</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Covers</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Year</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Added</TableCell>
                  <TableCell align="right" sx={{ width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Chip size="small" label={r.cla}
                        sx={{ fontWeight: 700, height: 22 }} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.sub}</TableCell>
                    {/* What this assignment actually reaches. The first
                        chip is the rule; the rest are the exam types the
                        subject is registered for, so it is visible at a
                        glance whether the mock is included — the thing
                        that could not be told before. */}
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center"
                        sx={{ flexWrap: 'wrap', gap: 0.4 }}>
                        <Chip size="small"
                          label={stateLabel(r.exam_state)}
                          color={r.exam_state ? 'secondary' : 'default'}
                          variant={r.exam_state ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 700, height: 20, fontSize: 11 }} />
                        {String(r.registered_states || '')
                          .split('|').filter(Boolean)
                          .filter(s => !r.exam_state || s === r.exam_state)
                          .map(s => (
                            <Chip key={s} size="small" variant="outlined" label={s}
                              sx={{ fontWeight: 600, height: 19, fontSize: 10.5,
                                color: 'text.secondary' }} />
                          ))}
                        {!r.registered_states && (
                          <Typography variant="caption" color="warning.main" sx={{ fontWeight: 700 }}>
                            not registered this year
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{r.acc_yer}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                      {r.created_at ? String(r.created_at).slice(0, 10) : ''}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove this assignment">
                        <IconButton color="error" size="small" onClick={() => handleRemove(r)}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
